/**
 * A stated degradation, BELOW the content it qualifies.
 *
 * Below, because a notice is a footnote about what you just read — a folded
 * count, a filter the vendor dropped, a lane that answered late. A caution
 * that must be read FIRST is a hazard, and that is a different component with
 * a different position.
 */
"use client";
import type { ReactElement, ReactNode } from "react";
import { useCresultcardNestedRadius } from "./cresultcard.radius";
import {
  crcToneAlpha,
  CRC_R_PILL,
  CRC_TONE_COLOR,
  CRC_TYPE_STYLE,
} from "./cresultcard.tokens";
import { CresultcardTextStep, CresultcardTone } from "./cresultcard.types";

const RAIL_WIDTH = 3;
/**
 * The tint and the hairline, as real alphas. They used to be two-digit hex
 * suffixes appended to the tone colour, which produced valid CSS for the three
 * hex tones and `rgba(...)12` — an invalid declaration the browser drops — for
 * `Neutral` and `Muted`. Two of five tones drew no surface at all.
 */
const TINT_ALPHA = 0.07;
const HAIRLINE_ALPHA = 0.18;

interface CresultcardNoticeProps {
  readonly children: ReactNode;
  readonly tone?: CresultcardTone;
}

export function CresultcardNotice({
  children,
  tone = CresultcardTone.Caution,
}: CresultcardNoticeProps): ReactElement {
  const color = CRC_TONE_COLOR[tone];
  /**
   * The rung below its holder's, like every other nested surface. It was pinned
   * to `CRC_R_CORE`, which painted 9px inside the 9px core that holds it — a
   * notice concentric with nothing — and no holder could say otherwise. SPEC
   * §3.1: the rung comes from the context, never from a number here and never
   * from a pillar author.
   */
  const radius = useCresultcardNestedRadius();

  return (
    <div
      data-el="Cresultcard/Notice/Root"
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 7,
        minWidth: 0,
        paddingBlock: 7,
        paddingInline: 9,
        borderRadius: radius,
        backgroundColor: crcToneAlpha({ tone, alpha: TINT_ALPHA }),
        boxShadow: `inset 0 0 0 1px ${crcToneAlpha({ tone, alpha: HAIRLINE_ALPHA })}`,
      }}
    >
      <span
        aria-hidden
        style={{
          width: RAIL_WIDTH,
          alignSelf: "stretch",
          flexShrink: 0,
          borderRadius: CRC_R_PILL,
          opacity: 0.7,
          backgroundColor: color,
        }}
      />
      <span
        style={{
          ...CRC_TYPE_STYLE[CresultcardTextStep.Meta],
          fontSize: 10,
          color,
        }}
      >
        {children}
      </span>
    </div>
  );
}
